// Public Delete Account page — accessible without signing in.
// Required for Apple App Store / Google Play / Microsoft Store submission.
// Users provide email + password + explicit DELETE confirmation to remove
// their account and every associated download / ambient capture.

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { AlertTriangle, ArrowLeft, Trash2, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import Disclaimer from '../components/Disclaimer';
import SelahBrandMark from '../components/SelahBrandMark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const canSubmit =
    email.trim().length > 3 && password.length > 0 && confirmation === 'DELETE' && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${BACKEND_URL}/api/auth/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password, confirmation }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || 'Deletion failed. Please check your credentials.');
      }
      toast.success('Account permanently deleted', {
        description: 'All your downloads, ambient captures, and profile data have been removed.',
      });
      // Small delay so the toast is visible before we route
      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      toast.error('Could not delete account', {
        description: err?.message || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-[var(--sd-bg)]">
      <div className="flex-1 hero-gradient">
        <div className="max-w-xl mx-auto px-4 sm:px-6 py-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[var(--sd-muted-fg)] hover:text-[var(--sd-fg)] mb-6"
            data-testid="delete-back-link"
          >
            <ArrowLeft size={14} />
            Back to Selah Downloads
          </Link>

          <div className="flex items-center gap-3 mb-4">
            <SelahBrandMark size={44} sparkleSize={13} />
            <div>
              <p className="text-xs uppercase tracking-wider text-[#C88484] font-semibold">Danger zone</p>
              <h1 className="text-3xl font-brand text-[var(--sd-fg)]">Delete your account</h1>
            </div>
          </div>

          <p className="text-sm text-[var(--sd-fg-secondary)] mb-6 leading-relaxed">
            No login required. Once you submit this form and we verify your credentials,
            your account, every download you’ve saved, every ambient capture, and all
            associated data will be <span className="text-[var(--sd-fg)] font-semibold">permanently removed</span> from our servers.
            This action cannot be undone.
          </p>

          <Card className="glass-card p-6" data-testid="delete-account-form-card">
            <div className="flex items-start gap-2 p-3 rounded-lg border border-[#C88484]/40 bg-[#C88484]/8 mb-6">
              <AlertTriangle size={16} className="text-[#C88484] mt-0.5 flex-shrink-0" />
              <p className="text-xs text-[var(--sd-fg-secondary)] leading-relaxed">
                If you just want to cancel your subscription and keep your downloads,
                sign in and go to Settings → Subscription instead. Deleting the
                account removes everything.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="del-email" className="block text-sm font-medium mb-1.5 text-[var(--sd-fg)]">
                  Email
                </label>
                <Input
                  id="del-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  disabled={submitting}
                  data-testid="delete-email-input"
                />
              </div>
              <div>
                <label htmlFor="del-password" className="block text-sm font-medium mb-1.5 text-[var(--sd-fg)]">
                  Password
                </label>
                <Input
                  id="del-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  disabled={submitting}
                  data-testid="delete-password-input"
                />
              </div>
              <div>
                <label htmlFor="del-confirm" className="block text-sm font-medium mb-1.5 text-[var(--sd-fg)]">
                  Type <span className="font-mono text-[#C88484] font-bold">DELETE</span> to confirm
                </label>
                <Input
                  id="del-confirm"
                  type="text"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder="DELETE"
                  autoComplete="off"
                  autoCapitalize="characters"
                  disabled={submitting}
                  data-testid="delete-confirmation-input"
                />
              </div>

              <Button
                type="submit"
                disabled={!canSubmit}
                data-testid="delete-submit-button"
                className="w-full bg-[#C88484] hover:bg-[#B47575] text-white font-semibold disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Deleting…
                  </>
                ) : (
                  <>
                    <Trash2 size={16} className="mr-2" />
                    Permanently delete my account
                  </>
                )}
              </Button>

              <div className="flex items-start gap-2 text-xs text-[var(--sd-muted-fg)] pt-2 border-t border-[var(--sd-border)] mt-4">
                <ShieldAlert size={12} className="text-[var(--sd-primary-dark)] mt-0.5 flex-shrink-0" />
                <p>
                  Need help instead? Reach us via the{' '}
                  <Link to="/support" className="text-[var(--sd-primary-dark)] hover:underline">
                    Support page
                  </Link>
                  .
                </p>
              </div>
            </form>
          </Card>
        </div>
      </div>
      <Disclaimer />
    </div>
  );
};

export default DeleteAccount;
